import { colors, hexToRgba, intl, ui } from '@beelday/common'
import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { ReactNode } from 'react'

/**
 * Building blocks shared by every phone screen. Sizes follow DESIGN.md:
 * type 12/14/16/20/24 (48 for the one display number), spacing in 4px steps,
 * radius 8 for controls, 16 for cards, 24 for pills.
 */

/**
 * Tablets (768-1023px) get the same shell with the content kept to a
 * readable column: full-width option rows and buttons across 800px of glass
 * read as a stretched phone. Phones are unaffected - every tablet rule sits
 * behind `TABLET`.
 */
const TABLET_COLUMN = 640

/**
 * A tablet is wide *and* tall. A phone held sideways is as wide as a small
 * tablet (844px) but about 390px tall, and the tablet rules - centred resting
 * screens, larger type - leave it no room, so it keeps the phone layout.
 */
const TABLET = '@media (min-width: 768px) and (min-height: 600px)'

/** A phone held sideways: resting screens start near the top. */
const SHORT = '@media (max-height: 500px)'

/** Room left under scrolling content for the floating boardlet button. */
const FAB_CLEARANCE = 96
/** ...and for a primary action bar on top of it. */
const CTA_CLEARANCE = 128

/** A padded column for one state of the stage. */
export const Screen = styled.div<{ $withCta?: boolean }>`
	flex: 1;
	display: flex;
	flex-direction: column;
	padding: 24px 20px
		${props => (props.$withCta ? CTA_CLEARANCE : FAB_CLEARANCE)}px;

	${TABLET} {
		width: 100%;
		max-width: ${TABLET_COLUMN + 40}px;
		margin: 0 auto;
		padding-top: 40px;
	}
`

export const Eyebrow = styled.div`
	align-self: flex-start;
	max-width: 100%;
	margin-bottom: 16px;
	padding: 4px 12px;
	border-radius: 24px;
	background: ${colors.indigoBlue10};
	color: ${colors.indigoBlue};
	font-size: 12px;
	font-weight: 700;
	overflow-wrap: anywhere;
`

export const Question = styled.h2`
	margin: 0 0 24px;
	font-size: 20px;
	font-weight: 700;
	line-height: 1.4;
	color: ${colors.blackish};
	overflow-wrap: anywhere;

	${TABLET} {
		font-size: 24px;
	}
`

export const Hint = styled.p`
	margin: 16px 0 0;
	font-size: 12px;
	line-height: 1.5;
	color: ${colors.heather};
`

export const CenteredHint = styled(Hint)`
	text-align: center;
`

const ProgressRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 8px;
	font-size: 12px;
	font-weight: 500;
	color: ${colors.heather};
`

const ProgressTrack = styled.div`
	height: 4px;
	margin-bottom: 20px;
	overflow: hidden;
	border-radius: 4px;
	background: ${colors.borderLightGray};
`

const ProgressFill = styled.div<{ $percent: number }>`
	width: ${props => props.$percent}%;
	height: 100%;
	border-radius: 4px;
	background: ${colors.indigoBlue};
	transition: width 0.3s ease;

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
`

type ProgressProps = {
	/** "Question 2 of 5". */
	label: ReactNode
	/** Shown opposite the label - a countdown, for one. */
	aside?: ReactNode
	/** How much is behind the participant, 0-100. */
	percent: number
}

/** Where a participant is in a multi-question activity. */
export const Progress = ({
	label,
	aside,
	percent,
}: ProgressProps): JSX.Element => (
	<>
		<ProgressRow>
			<span>{label}</span>
			{aside}
		</ProgressRow>
		<ProgressTrack>
			<ProgressFill $percent={percent} />
		</ProgressTrack>
	</>
)

export const QuestionImage = styled.img`
	display: block;
	max-width: 100%;
	max-height: 220px;
	margin: -8px auto 20px;
	border-radius: 8px;
`

/** Read out by screen readers, never painted. */
export const ScreenReaderOnly = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	clip: rect(0 0 0 0);
	white-space: nowrap;
`

export const Options = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`

/** How a revealed answer turned out, for quizzes that show it. */
export type OptionResult = 'correct' | 'incorrect'

const RESULT_COLOR: Record<OptionResult, string> = {
	correct: colors.greenishCyan,
	incorrect: colors.lipstick,
}

const RESULT_BACKGROUND: Record<OptionResult, string> = {
	correct: hexToRgba(colors.greenishCyan, 0.12),
	incorrect: hexToRgba(colors.lipstick, 0.08),
}

type OptionLook = { $selected: boolean; $result?: OptionResult }

const optionBorder = ({ $selected, $result }: OptionLook): string =>
	$result
		? RESULT_COLOR[$result]
		: $selected
		? colors.indigoBlue
		: colors.borderLightGray

const optionBackground = ({ $selected, $result }: OptionLook): string =>
	$result
		? RESULT_BACKGROUND[$result]
		: $selected
		? colors.indigoBlue05
		: colors.white

const OptionButton = styled.button<OptionLook>`
	display: flex;
	align-items: center;
	gap: 12px;
	width: 100%;
	min-height: 52px;
	padding: 12px 16px;
	border: 2px solid ${optionBorder};
	border-radius: 16px;
	background: ${optionBackground};
	font-family: inherit;
	font-size: 14px;
	font-weight: 500;
	line-height: 1.4;
	color: ${colors.blackish};
	text-align: left;
	cursor: pointer;
	overflow-wrap: anywhere;

	${TABLET} {
		min-height: 60px;
		font-size: 16px;
	}

	&:disabled {
		opacity: ${props => (props.$result ? 1 : 0.5)};
		cursor: default;
	}
`

const Mark = styled.span<OptionLook & { $square: boolean }>`
	width: 20px;
	height: 20px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: ${props => (props.$square ? '4px' : '50%')};
	border: 2px solid
		${props =>
			props.$result
				? RESULT_COLOR[props.$result]
				: props.$selected
				? colors.indigoBlue
				: colors.borderGray};
	background: ${props =>
		props.$result
			? RESULT_COLOR[props.$result]
			: props.$selected
			? colors.indigoBlue
			: 'transparent'};
`

/** The tick `answer-buttons.tsx` paints into its selection ellipse. */
export const Tick = ({ size = 12 }: { size?: number }): JSX.Element => (
	<svg
		width={size}
		height={(size * 7.4) / 10.5}
		viewBox="0 0 10.484 7.398"
		aria-hidden
	>
		<path
			fill="none"
			stroke={colors.white}
			strokeWidth="1.5"
			d="M0.53 2.721l3.617 3.617 5.807-5.807"
		/>
	</svg>
)

type OptionProps = {
	selected: boolean
	/** Checkbox look for multi-select questions, radio otherwise. */
	multiple?: boolean
	disabled?: boolean
	/** Revealed outcome; colours the option and is read out after its text. */
	result?: OptionResult
	/** What `result` sounds like to a screen reader. */
	resultLabel?: string
	onClick?: () => void
	children: ReactNode
}

export const Option = ({
	selected,
	multiple = false,
	disabled,
	result,
	resultLabel,
	onClick,
	children,
}: OptionProps): JSX.Element => (
	<OptionButton
		type="button"
		role={multiple ? 'checkbox' : 'radio'}
		aria-checked={selected}
		$selected={selected}
		$result={result}
		disabled={disabled}
		onClick={onClick}
	>
		<Mark $selected={selected} $result={result} $square={multiple}>
			{selected || result ? <Tick /> : null}
		</Mark>
		<span>
			{children}
			{result && resultLabel ? (
				<ScreenReaderOnly>{` — ${resultLabel}`}</ScreenReaderOnly>
			) : null}
		</span>
	</OptionButton>
)

/** The verdict above a revealed question. */
export const Feedback = styled.p<{ $tone: 'good' | 'bad' }>`
	margin: 0 0 16px;
	padding: 12px 16px;
	border-radius: 8px;
	background: ${props =>
		props.$tone === 'good'
			? hexToRgba(colors.greenishCyan, 0.13)
			: hexToRgba(colors.lipstick, 0.1)};
	color: ${props =>
		props.$tone === 'good' ? colors.blackish : colors.lipstick};
	font-size: 14px;
	font-weight: 700;
	line-height: 1.4;
`

/** The strip above the action where scrolled content fades out. */
const CTA_FADE = 32

/**
 * The primary action, pinned to the bottom of the stage. The right padding
 * keeps it clear of the floating boardlet button. Solid behind the button,
 * fading only above it: content scrolling underneath never shows through the
 * button itself.
 */
export const CtaBar = styled.div`
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 2;
	display: flex;
	gap: 12px;
	padding: ${CTA_FADE}px 88px 20px 20px;
	background: linear-gradient(
		to top,
		${colors.athensGray} calc(100% - ${CTA_FADE}px),
		${hexToRgba(colors.athensGray, 0)}
	);

	/* Line the action up with the content column, still clear of the FAB. */
	${TABLET} {
		padding: ${CTA_FADE}px max(88px, calc((100% - ${TABLET_COLUMN}px) / 2)) 24px
			max(20px, calc((100% - ${TABLET_COLUMN}px) / 2));
	}
`

/**
 * `ui.Button`, stretched to its slot. Sentence case and a larger label than
 * the desktop button, as in the phone mockup: on a phone it is the one thing
 * to tap.
 */
export const WideButton = styled(ui.Button)`
	flex: 1;
	width: 100%;
	min-width: 0;
	padding: 0 16px;
	font-size: 15px;
	font-weight: 700;
	text-transform: none;
`

type ActionProps = {
	busy?: boolean
	disabled?: boolean
	secondary?: boolean
	onClick: () => void
	children: ReactNode
	testId?: string
}

/**
 * A full-width action. Busy shows the shared button spinner rather than
 * silently disabling (DESIGN.md: never disable without feedback).
 */
export const Action = ({
	busy = false,
	disabled = false,
	secondary = false,
	onClick,
	children,
	testId,
}: ActionProps): JSX.Element => (
	<WideButton
		type="button"
		backgroundColor={secondary ? 'white' : 'indigoBlue'}
		color={secondary ? 'indigoBlue' : 'white'}
		borderColor={secondary ? 'indigoBlue' : undefined}
		disabled={disabled || busy}
		aria-busy={busy}
		onClick={onClick}
		data-testid={testId}
	>
		{busy ? <ui.ButtonSpinner color={colors.white} /> : children}
	</WideButton>
)

export const TextInput = styled.input`
	width: 100%;
	padding: 12px 16px;
	border: 2px solid ${colors.borderLightGray};
	border-radius: 8px;
	font-family: inherit;
	/* 16px keeps iOS Safari from zooming the page on focus. */
	font-size: 16px;
	color: ${colors.blackish};
	background: ${colors.white};
	outline: none;

	&:focus {
		border-color: ${colors.indigoBlue};
	}

	&::placeholder {
		color: ${colors.heather};
	}
`

export const CharCount = styled.div`
	margin-top: 4px;
	text-align: right;
	font-size: 12px;
	color: ${colors.heather};
`

const Centered = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 88px 32px ${FAB_CLEARANCE}px;
	text-align: center;

	/* Clears the orb's outer pulse ring (28px outside it). */
	${SHORT} {
		padding-top: 40px;
	}

	/* A tall tablet screen reads better with the resting state centred. */
	${TABLET} {
		justify-content: center;
		padding-top: 40px;
	}
`

const Badge = styled.div<{ $background: string }>`
	width: 80px;
	height: 80px;
	margin-bottom: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background: ${props => props.$background};
`

const StateTitle = styled.h2`
	margin: 0 0 8px;
	font-size: 20px;
	font-weight: 700;
	color: ${colors.blackish};
	overflow-wrap: anywhere;
`

const StateText = styled.div`
	max-width: 280px;
	font-size: 14px;
	line-height: 1.5;
	color: ${colors.heather};
	overflow-wrap: anywhere;
`

export const StateActions = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 24px;
`

export const SentCheck = (): JSX.Element => (
	<svg width="36" height="36" viewBox="0 0 34 34" aria-hidden>
		<circle cx="17" cy="17" r="17" fill={colors.greenishCyan} />
		<g transform="translate(11.2 13.2) scale(1.12)">
			<Tick size={10.5} />
		</g>
	</svg>
)

type ConfirmationProps = {
	title: ReactNode
	description?: ReactNode
	/** `null` for a text-only state. */
	icon?: ReactNode | null
	background?: string
	children?: ReactNode
	testId?: string
}

/** A centred outcome: badge, title, one line, optional actions. */
export const Confirmation = ({
	title,
	description,
	icon = <SentCheck />,
	background = colors.greenishCyan02,
	children,
	testId,
}: ConfirmationProps): JSX.Element => (
	<Centered data-testid={testId} role="status">
		{icon ? <Badge $background={background}>{icon}</Badge> : null}
		<StateTitle>{title}</StateTitle>
		{description ? <StateText>{description}</StateText> : null}
		{children}
	</Centered>
)

const ringPulse = keyframes`
	0% { opacity: .5; transform: scale(0.85); }
	100% { opacity: 0; transform: scale(1.15); }
`

const Orb = styled.div`
	position: relative;
	width: 108px;
	height: 108px;
	margin-bottom: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
`

const Ring = styled.div<{ $inset: number; $delay: number }>`
	position: absolute;
	inset: ${props => -props.$inset}px;
	border-radius: 50%;
	border: 2px solid ${hexToRgba(colors.indigoBlue, 0.16)};
	animation: ${ringPulse} 2.4s ease-out infinite ${props => props.$delay}s;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
		opacity: 0.4;
	}
`

const OrbFill = styled.div<{ $background: string }>`
	position: relative;
	width: 108px;
	height: 108px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background: ${props => props.$background};
`

const RestTitle = styled.h2`
	margin: 0 0 12px;
	font-size: 24px;
	font-weight: 700;
	color: ${colors.blackish};
	overflow-wrap: anywhere;
`

const RestEyebrow = styled(Eyebrow)`
	align-self: center;
`

/** A picture of the moment, where the quieter screens have an icon. */
const RestPicture = styled.img`
	display: block;
	width: min(280px, 72vw);
	height: auto;
	margin-bottom: 20px;
	user-select: none;
`

type RestProps = {
	/** In the pulsing orb, unless there is an `illustration`. */
	icon?: ReactNode
	/** Decorative: a picture of the moment instead of the orb. */
	illustration?: string
	/** A short label above the title: what kind of moment this is. */
	eyebrow?: ReactNode
	title: ReactNode
	description?: ReactNode
	background?: string
	pulse?: boolean
	children?: ReactNode
	testId?: string
}

/** The calm, centred screen for idle, media, waiting and break. */
export const Rest = ({
	icon,
	illustration,
	eyebrow,
	title,
	description,
	background = colors.indigoBlue05,
	pulse = true,
	children,
	testId,
}: RestProps): JSX.Element => (
	<Centered data-testid={testId}>
		{illustration ? (
			<RestPicture
				src={illustration}
				alt=""
				aria-hidden="true"
				draggable={false}
			/>
		) : (
			<Orb>
				{pulse ? (
					<>
						<Ring $inset={14} $delay={0} />
						<Ring $inset={28} $delay={0.6} />
					</>
				) : null}
				<OrbFill $background={background}>{icon}</OrbFill>
			</Orb>
		)}
		{eyebrow ? <RestEyebrow>{eyebrow}</RestEyebrow> : null}
		<RestTitle>{title}</RestTitle>
		{description ? <StateText>{description}</StateText> : null}
		{children}
	</Centered>
)

export const Card = styled.div`
	padding: 20px;
	border: 1px solid ${colors.borderLightGray};
	border-radius: 16px;
	background: ${colors.white};
`

export const CardLabel = styled.div`
	margin-bottom: 4px;
	font-size: 12px;
	font-weight: 700;
	color: ${colors.heather};
`

export const CardTitle = styled.div`
	margin-bottom: 8px;
	font-size: 16px;
	font-weight: 700;
	line-height: 1.4;
	color: ${colors.blackish};
	overflow-wrap: anywhere;
`

export const CardText = styled.div`
	font-size: 14px;
	line-height: 1.5;
	color: ${colors.brightBlackish};
	white-space: pre-line;
	overflow-wrap: anywhere;
`

/** Shown under a form whose answer did not reach the server. */
const ErrorMessage = styled.p`
	margin: 16px 0 0;
	padding: 12px 16px;
	border-radius: 8px;
	background: ${hexToRgba(colors.lipstick, 0.1)};
	color: ${colors.lipstick};
	font-size: 14px;
	font-weight: 500;
	line-height: 1.4;
`

export const SendError = (): JSX.Element => (
	<ErrorMessage role="alert">
		<intl.Translate>in_person.mobile.error.send</intl.Translate>
	</ErrorMessage>
)
